import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Check, X } from "lucide-react";
import type { MedicalTextIssue } from "@/types/issues";

interface IssueCardProps {
  issue: MedicalTextIssue;
  onResolve: (issueId: string) => void;
  onReopen: (issueId: string) => void;
  onClick: () => void;
  isSelected?: boolean;
  isResolving?: boolean;
  isReopening?: boolean;
  commentCount?: number;
  userProfile?: {
    display_name: string;
    display_color: string;
  };
  resolvedByProfile?: {
    display_name: string;
    display_color: string;
  };
}

export function IssueCard({
  issue,
  onResolve,
  onReopen,
  onClick,
  isSelected = false,
  isResolving = false,
  isReopening = false,
  commentCount = 0,
  userProfile,
  resolvedByProfile,
}: IssueCardProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const isResolved = issue.status === "resolved";
  const truncatedDescription =
    issue.description && issue.description.length > 100
      ? `${issue.description.substring(0, 100)}...`
      : issue.description;

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
        isSelected ? "ring-2 ring-blue-500 border-blue-300" : "border-gray-200"
      } ${isResolved ? "bg-gray-50" : "bg-white"}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge variant={isResolved ? "secondary" : "default"}>{isResolved ? "Resolved" : "Active"}</Badge>
          {commentCount > 0 && (
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <MessageSquare className="w-4 h-4" />
              <span>{commentCount}</span>
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {!isResolved ? (
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onResolve(issue.id);
              }}
              disabled={isResolving}
            >
              <Check className="w-3 h-3 mr-1" />
              {isResolving ? "Resolving..." : "Resolve"}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReopen(issue.id);
              }}
              disabled={isReopening}
            >
              <X className="w-3 h-3 mr-1" />
              {isReopening ? "Reopening..." : "Reopen"}
            </Button>
          )}
        </div>
      </div>

      <h3 className="font-medium text-gray-900 mb-2">{issue.title}</h3>

      {issue.description && (
        <div className="mb-3">
          <p className="text-sm text-gray-600">{showFullDescription ? issue.description : truncatedDescription}</p>
          {issue.description.length > 100 && (
            <button
              type="button"
              className="text-blue-600 text-sm hover:underline mt-1"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullDescription(!showFullDescription);
              }}
            >
              {showFullDescription ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          {userProfile && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: userProfile.display_color }} />
              <span>Created by {userProfile.display_name}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span>Created {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}</span>
          {isResolved && issue.resolved_at && resolvedByProfile && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: resolvedByProfile.display_color }} />
              <span>
                Resolved by {resolvedByProfile.display_name}{" "}
                {formatDistanceToNow(new Date(issue.resolved_at), { addSuffix: true })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
